import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import CouponQrModal from '../components/CouponQrModal';
import InfoTip from '../components/InfoTip';
import { MAX_LEN as PREVIEW_MAX_LEN, previewText } from '../utils/servicePreview';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Pagination, ProgressBar, SearchInput, TableSkeleton, Tabs,
} from '../components/ui';

// Coupons — admin-defined activation passcodes. When an unregistered sender messages
// any channel the platform serves with a coupon code, proxy-channels hits the internal
// /redeem-coupon endpoint, which creates a user against that channel on the coupon's
// chosen tier and pairs the address the code arrived from.
//
// Which channels accept coupons is set per channel on the Channels page (default: all of
// them). On Telegram the pool bot that received the message is also pinned to the new
// user, since that channel is the one with a credential pool.

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }) + ' UTC';
  } catch { return iso; }
}

// "19 Jul 2026, 14:05 UTC", the form the table shows. A month name cannot be misread as
// D/M vs M/D. formatDate's full stamp stays in the search index next to it, so a date
// pasted in either form still matches.
function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }) + ' UTC';
}

function toDateTimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function fromDateTimeLocal(value) {
  if (!value) return null;
  return new Date(value + 'Z').toISOString();
}

function randomCode(length = 10) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const out = new Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(length);
    crypto.getRandomValues(buf);
    for (let i = 0; i < length; i++) out[i] = alphabet[buf[i] % alphabet.length];
  } else {
    for (let i = 0; i < length; i++) out[i] = alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out.join('');
}

const EMPTY_DRAFT = {
  code: '',
  description: '',
  max_uses: '',
  expires_at: '',
  tier_id: '',
  duration_days: '',
  pre_expiry_broadcast_days: '',
  pre_expiry_broadcast_message: '',
};

const STATUS_TONE = { active: 'ok', expired: 'danger', exhausted: 'warn' };
const STATUS_TEXT = { active: 'Active', expired: 'Expired', exhausted: 'Exhausted' };

function CouponModal({ mode, initial, tiers, onSubmit, onCancel }) {
  // Modal lifts its own form state. `initial` is the existing coupon row when
  // editing; we map it into the editable shape on first render. On `Save`
  // the parent decides whether the payload becomes a POST or a PUT.
  const seed = initial
    ? {
        code: initial.code || '',
        description: initial.description || '',
        max_uses: initial.max_uses == null ? '' : String(initial.max_uses),
        expires_at: toDateTimeLocal(initial.expires_at),
        tier_id: initial.tier_id == null ? '' : String(initial.tier_id),
        duration_days: initial.duration_days == null ? '' : String(initial.duration_days),
        pre_expiry_broadcast_days: initial.pre_expiry_broadcast_days == null
          ? '' : String(initial.pre_expiry_broadcast_days),
        pre_expiry_broadcast_message: initial.pre_expiry_broadcast_message || '',
      }
    : EMPTY_DRAFT;

  const [draft, setDraft] = useState(seed);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // Split the form across two tabs so the lifecycle settings (newer,
  // less-common features) don't clutter the basic case.
  const [activeTab, setActiveTab] = useState('basic');
  const isEdit = mode === 'edit';
  const codeReadOnly = isEdit;

  // The pre-expiry warning UI only makes sense when a duration is set —
  // collapse it otherwise so the lifecycle tab stays short for the
  // common case (coupon with a duration but no warning).
  const hasDuration = draft.duration_days !== '' && Number(draft.duration_days) > 0;

  // Helper: bubble an error toast and jump to the tab that contains the
  // offending field so the admin sees the input they need to fix.
  const failOnTab = (tab, msg) => {
    setActiveTab(tab);
    toast.error(msg);
  };

  const handleSubmit = async () => {
    const code = (draft.code || '').trim();
    if (!isEdit && !code) { failOnTab('basic', 'Code is required'); return; }
    let max_uses_num = null;
    if (draft.max_uses !== '') {
      const n = Number(draft.max_uses);
      if (!Number.isInteger(n) || n < 1) {
        failOnTab('basic', 'Max uses must be ≥ 1 (or empty for unlimited)');
        return;
      }
      max_uses_num = n;
    }
    const tier_id_num = draft.tier_id !== '' ? Number(draft.tier_id) : null;
    const expires_iso = fromDateTimeLocal(draft.expires_at);

    // Lifecycle: only validate + send when duration_days is set. Clearing
    // duration also clears the pre-expiry pair (both backend and frontend
    // enforce this triad so a partial state can't be saved).
    let duration_num = null;
    if (draft.duration_days !== '') {
      const n = Number(draft.duration_days);
      if (!Number.isInteger(n) || n < 1) {
        failOnTab('lifecycle', 'Duration must be ≥ 1 day (or empty for none)');
        return;
      }
      duration_num = n;
    }

    let pre_days_num = null;
    let pre_msg = null;
    if (duration_num !== null && (draft.pre_expiry_broadcast_days !== '' || draft.pre_expiry_broadcast_message.trim() !== '')) {
      // The admin started filling the pre-expiry section. Require both
      // halves to be filled before allowing save.
      if (draft.pre_expiry_broadcast_days === '') {
        failOnTab('lifecycle', 'Pre-expiry: enter the number of days before expiry to send the warning');
        return;
      }
      if (draft.pre_expiry_broadcast_message.trim() === '') {
        failOnTab('lifecycle', 'Pre-expiry: warning message cannot be empty');
        return;
      }
      const n = Number(draft.pre_expiry_broadcast_days);
      if (!Number.isInteger(n) || n < 1) {
        failOnTab('lifecycle', 'Pre-expiry days must be ≥ 1');
        return;
      }
      if (n >= duration_num) {
        failOnTab('lifecycle', 'Pre-expiry days must be less than duration');
        return;
      }
      if (draft.pre_expiry_broadcast_message.length > PREVIEW_MAX_LEN) {
        failOnTab('lifecycle', `Pre-expiry message is too long (max ${PREVIEW_MAX_LEN} chars)`);
        return;
      }
      pre_days_num = n;
      pre_msg = draft.pre_expiry_broadcast_message.trim();
    }

    const body = {
      description: draft.description || null,
    };
    if (!isEdit) {
      body.code = code;
      body.max_uses = max_uses_num;
      body.expires_at = expires_iso;
      body.tier_id = tier_id_num;
      body.duration_days = duration_num;
      body.pre_expiry_broadcast_days = pre_days_num;
      body.pre_expiry_broadcast_message = pre_msg;
    } else {
      // PUT semantics: explicit null on the original means we want to
      // clear; missing means leave alone. We send sentinels for that.
      if (max_uses_num === null && initial.max_uses != null) body.clear_max_uses = true;
      else if (max_uses_num !== null) body.max_uses = max_uses_num;

      if (expires_iso === null && initial.expires_at) body.clear_expires_at = true;
      else if (expires_iso !== null) body.expires_at = expires_iso;

      if (tier_id_num === null && initial.tier_id != null) body.clear_tier_id = true;
      else if (tier_id_num !== null) body.tier_id = tier_id_num;

      if (duration_num === null && initial.duration_days != null) body.clear_duration_days = true;
      else if (duration_num !== null) body.duration_days = duration_num;

      if (pre_days_num === null && initial.pre_expiry_broadcast_days != null) body.clear_pre_expiry_broadcast_days = true;
      else if (pre_days_num !== null) body.pre_expiry_broadcast_days = pre_days_num;

      if (pre_msg === null && initial.pre_expiry_broadcast_message) body.clear_pre_expiry_broadcast_message = true;
      else if (pre_msg !== null) body.pre_expiry_broadcast_message = pre_msg;
    }

    setBusy(true);
    try {
      await onSubmit(body);
    } finally {
      setBusy(false);
    }
  };

  // A coupon without a tier is redeemed onto whichever tier is the default at that moment
  // (services/coupons.py), so the option names that tier rather than a fixed one.
  const defaultTier = tiers.find(t => t.is_default);
  const charsLeft = PREVIEW_MAX_LEN - draft.pre_expiry_broadcast_message.length;
  const hasMessage = draft.pre_expiry_broadcast_message.trim() !== '';

  return (
    <Modal
      open
      onClose={() => { if (!busy) onCancel(); }}
      title={isEdit ? `Edit coupon ${initial.code}` : 'New coupon'}
      maxWidth={600}
    >
      <Tabs
        tabs={[
          { value: 'basic', label: 'Basics', icon: 'coupons' },
          { value: 'lifecycle', label: 'Lifecycle', icon: 'clock' },
        ]}
        value={activeTab}
        onChange={setActiveTab}
        label="Coupon settings"
        className="mb-5"
      />

      {activeTab === 'basic' && (
        <div role="tabpanel" aria-label="Basics">
          {!isEdit && (
            <Field label="Code" htmlFor="coupon-code" required
                   hint="4–100 characters: letters, digits, hyphens and underscores.">
              <div className="input-group">
                <input id="coupon-code" className="form-input mono" value={draft.code}
                       onChange={e => setDraft(d => ({ ...d, code: e.target.value }))}
                       placeholder="couponcode" autoFocus autoComplete="off" spellCheck={false}
                       disabled={codeReadOnly} />
                <button type="button" className="btn btn-secondary"
                        onClick={() => setDraft(d => ({ ...d, code: randomCode(10) }))}
                        title="Generate a random 10-character code">
                  <Icon name="refresh" /> Generate
                </button>
              </div>
            </Field>
          )}

          <Field label="Description" htmlFor="coupon-description">
            <input id="coupon-description" className="form-input" value={draft.description}
                   onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                   placeholder="What's this coupon for? (optional)" />
          </Field>

          <div className="form-row">
            <Field label="Max uses" htmlFor="coupon-max-uses" hint="Leave empty for unlimited redemptions.">
              <input id="coupon-max-uses" type="number" min={1} className="form-input num"
                     value={draft.max_uses}
                     onChange={e => setDraft(d => ({ ...d, max_uses: e.target.value }))}
                     placeholder="Unlimited" />
            </Field>
            <Field label="Expires at (UTC)" htmlFor="coupon-expires" hint="Leave empty for no expiry.">
              <input id="coupon-expires" type="datetime-local" className="form-input"
                     value={draft.expires_at}
                     onChange={e => setDraft(d => ({ ...d, expires_at: e.target.value }))} />
            </Field>
          </div>

          <Field label="Tier" htmlFor="coupon-tier" className="mb-0"
                 hint="Every user redeemed via this coupon is assigned this tier.">
            <select id="coupon-tier" className="form-input" value={draft.tier_id}
                    onChange={e => setDraft(d => ({ ...d, tier_id: e.target.value }))}>
              <option value="">
                {defaultTier ? `Default tier (currently ${defaultTier.name})` : 'Default tier'}
              </option>
              {tiers.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </Field>
        </div>
      )}

      {activeTab === 'lifecycle' && (
        <div role="tabpanel" aria-label="Lifecycle">
          <Field
            label="Duration (days after activation)"
            htmlFor="coupon-duration"
            hint="When set, the user's agent is stopped this many days after redemption, as if they unsubscribed. Edits don't retro-affect users who already redeemed."
          >
            <input id="coupon-duration" type="number" min={1} className="form-input num"
                   value={draft.duration_days}
                   onChange={e => setDraft(d => ({ ...d, duration_days: e.target.value }))}
                   placeholder="No auto-stop" />
          </Field>

          {hasDuration ? (
            <div className="form-section mb-0">
              <div className="row gap-2 mb-1">
                <h4 className="text-base fw-600">Pre-expiry warning</h4>
                <Badge tone="neutral">Optional</Badge>
              </div>
              <p className="text-sm text-muted mb-4">
                Send the user a service message this many days before their agent stops.
                Both fields must be filled to enable; leave both empty to skip.
              </p>

              <Field label="Days before expiry" htmlFor="coupon-pre-days"
                     hint={`From 1 to ${Math.max(1, Number(draft.duration_days) - 1)}.`}>
                <input id="coupon-pre-days" type="number" min={1} className="form-input num"
                       value={draft.pre_expiry_broadcast_days}
                       onChange={e => setDraft(d => ({ ...d, pre_expiry_broadcast_days: e.target.value }))} />
              </Field>

              <Field label="Warning message" htmlFor="coupon-pre-message" className="mb-0"
                     hint="The [MESSAGGIO DI SERVIZIO] prefix is added automatically, just write the body.">
                <textarea id="coupon-pre-message" className="form-input"
                          rows={5}
                          maxLength={PREVIEW_MAX_LEN}
                          value={draft.pre_expiry_broadcast_message}
                          onChange={e => setDraft(d => ({ ...d, pre_expiry_broadcast_message: e.target.value }))} />
              </Field>

              <div className="row between mt-2">
                <button type="button" className="btn btn-secondary btn-sm"
                        onClick={() => setPreviewOpen(o => !o)}
                        disabled={!hasMessage}
                        aria-expanded={previewOpen && hasMessage}>
                  <Icon name={previewOpen && hasMessage ? 'eyeOff' : 'eye'} />
                  {previewOpen && hasMessage ? 'Hide preview' : 'Preview'}
                </button>
                <span className={`text-sm num ${charsLeft < 100 ? 'text-danger' : 'text-muted'}`}>
                  {charsLeft.toLocaleString()} chars left
                </span>
              </div>

              {previewOpen && hasMessage && (
                <div className="mt-3">
                  <p className="text-sm text-muted mb-2">
                    This is what the user will receive, on whichever channel they last
                    wrote from, the number of days you set before their access stops.
                  </p>
                  <pre className="code-block pre-wrap break m-0" style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {previewText(draft.pre_expiry_broadcast_message)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            // Without a duration there's no expiry to warn ahead of.
            // Surface that explicitly rather than just hiding the box.
            <div className="notice notice-info mb-0">
              <Icon name="info" />
              <div className="notice-body">Set a duration above to enable pre-expiry warning messages.</div>
            </div>
          )}
        </div>
      )}

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
          {busy
            ? <><span className="spinner-sm" /> Saving…</>
            : (isEdit ? 'Save coupon' : 'Create coupon')}
        </button>
      </div>
    </Modal>
  );
}

function Coupons() {
  const [rows, setRows] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);  // null | { mode: 'create' } | { mode: 'edit', initial: row }
  const [qrFor, setQrFor] = useState(null);  // the coupon row whose QR dialog is open
  // Search + pagination — mirrors the Users page UX. Filtering is
  // client-side because the coupon set is small (tens, maybe hundreds)
  // and we want truly instant per-keystroke matching across every
  // visible column, including derived ones (tier name, status badge)
  // that aren't searchable via the backend's simple SQL filter.
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirm, confirmDialog] = useConfirm();
  const PAGE_SIZE = 10;

  const load = async () => {
    try {
      const [cResp, tResp] = await Promise.all([
        apiFetch('/api/coupons'),
        apiFetch('/api/tiers'),
      ]);
      if (cResp.ok) setRows(await cResp.json()); else toast.error('Failed to load coupons');
      if (tResp.ok) {
        const tData = await tResp.json();
        // /api/tiers returns either an array or { tiers: [...] } depending on impl
        setTiers(Array.isArray(tData) ? tData : (tData.tiers || []));
      }
    } catch (e) { toast.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const tierName = (id) => {
    if (id == null) return '—';
    const t = tiers.find(x => x.id === id);
    return t ? t.name : `#${id}`;
  };

  const handleCreate = async (body) => {
    const ok = await callApi('/api/coupons', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, { successMsg: `Coupon "${body.code}" created`, errorMsg: 'Failed to create' });
    if (ok) { setModal(null); load(); }
  };

  const handleUpdate = async (id, body) => {
    const ok = await callApi(`/api/coupons/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }, { successMsg: 'Coupon updated', errorMsg: 'Failed to update' });
    if (ok) { setModal(null); load(); }
  };

  const remove = async (row) => {
    if (!await confirm({
      title: `Delete coupon ${row.code}?`,
      body: (
        <p>
          Users already created with it keep their accounts. This only removes the
          passcode, so nobody new can redeem it.
        </p>
      ),
      confirmLabel: 'Delete coupon',
    })) return;
    await callApi(`/api/coupons/${row.id}`, { method: 'DELETE' },
                  { successMsg: `Deleted "${row.code}"`, errorMsg: 'Failed to delete' });
    load();
  };

  // Status label for a row, used by both the badge AND the search index
  // (so typing "expired" filters down to expired coupons even though the
  // status isn't a literal DB column).
  const statusLabel = (row) => {
    if (row.is_expired) return 'expired';
    if (row.is_exhausted) return 'exhausted';
    return 'active';
  };

  const statusBadge = (row) => {
    const s = statusLabel(row);
    return <Badge tone={STATUS_TONE[s]} dot>{STATUS_TEXT[s]}</Badge>;
  };

  // Filter on every user-visible field. Case-insensitive substring match,
  // splits the query on whitespace so "active mark" finds an active
  // coupon whose code or description contains "mark".
  const filteredRows = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    const terms = q.split(/\s+/).filter(Boolean);
    return rows.filter(r => {
      const haystack = [
        r.code || '',
        r.description || '',
        tierName(r.tier_id) || '',
        statusLabel(r),
        r.duration_days != null ? String(r.duration_days) : '',
        r.max_uses != null ? String(r.max_uses) : 'infinite',
        String(r.current_uses ?? 0),
        formatDate(r.expires_at) || '',
        formatShortDate(r.expires_at),
      ].join(' ').toLowerCase();
      return terms.every(t => haystack.includes(t));
    });
  })();

  // Clamp page when filtering shrinks the result set below the current page.
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedRows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const openCreate = () => setModal({ mode: 'create' });

  return (
    <div>
      {/* Title, tip and lede lifted out of the card. They were the card's header and its
          first paragraph, which made the card the page and left nothing above it saying
          where you were. */}
      <PageHeader
        description="Passcodes that create an account. Someone messages the code on a channel that accepts coupons and gets a user on the tier you chose."
        actions={(
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" /> New coupon
          </button>
        )}
      >
        <h1 className="page-title">
          Coupons
          {!loading && <Badge tone="neutral" size="lg">{rows.length}</Badge>}
          {/* The lede already defines a coupon and says it creates a user on the tier you
              chose. Dropped as readable from the field labels: expiry and use limit are
              optional, and the two access fields are separate, one stopping the agent a set
              number of days after redemption and the other warning the user before it
              happens. */}
          <InfoTip label="About coupons" width={350}>
            <p>
              Which channels accept codes is set per channel on{' '}
              <strong>Channels</strong>. A code sent anywhere else is ignored.
            </p>
            <p>
              On Telegram the user is also bound to the specific pool bot they texted.
            </p>
            <p>
              Editing a coupon never changes users who have already redeemed it.
            </p>
          </InfoTip>
        </h1>
      </PageHeader>

      <div className="card card-flush">
        {loading ? (
          <TableSkeleton rows={6} cols={7} label="Loading coupons" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="coupons"
            title="No coupons yet"
            action={<button type="button" className="btn btn-primary" onClick={openCreate}>Create the first coupon</button>}
          >
            A coupon is a code someone sends on a channel to get an account on the tier you pick.
          </EmptyState>
        ) : (
          <>
            <div className="toolbar">
              {/* Filters every visible field per keystroke. Same UX shape as the Users page. */}
              <SearchInput
                value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                placeholder="Search code, description, tier, status…"
                label="Search coupons"
              />
              {search && (
                <span className="text-sm text-muted num">
                  {filteredRows.length} of {rows.length}
                </span>
              )}
            </div>

            {filteredRows.length === 0 ? (
              <EmptyState
                icon="search"
                title={`No coupons match “${search}”`}
                action={<button type="button" className="btn btn-secondary" onClick={() => setSearch('')}>Clear search</button>}
              >
                Search matches code, description, tier, status, uses, duration and expiry.
              </EmptyState>
            ) : (
              <div className="table-container">
                <table className="table table-stack">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Status</th>
                      <th className="num">Uses</th>
                      <th>Expires</th>
                      <th>Duration</th>
                      <th>Tier</th>
                      <th><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map(row => (
                      <tr key={row.id}>
                        <td data-label="Code" className="wrap">
                          <div style={{ maxWidth: 320, minWidth: 0 }}>
                            <span className="cell-primary mono">{row.code}</span>
                            {row.description && <span className="cell-sub">{row.description}</span>}
                          </div>
                        </td>
                        <td data-label="Status">{statusBadge(row)}</td>
                        <td data-label="Uses" className="num">
                          <div className="stack gap-1" style={{ minWidth: 88 }}>
                            <span className="text-sm">
                              <span className="fw-600">{row.current_uses}</span>
                              <span className="text-muted"> / {row.max_uses == null ? '∞' : row.max_uses}</span>
                            </span>
                            {row.max_uses != null && (
                              <ProgressBar value={row.current_uses} max={row.max_uses}
                                           label={`${row.current_uses} of ${row.max_uses} uses`} />
                            )}
                          </div>
                        </td>
                        <td data-label="Expires" className="nowrap">
                          {row.expires_at
                            ? <span className={`num text-sm${row.is_expired ? ' text-danger' : ''}`}
                                    title={formatDate(row.expires_at)}>
                                {formatShortDate(row.expires_at)}
                              </span>
                            : <span className="text-faint">Never</span>}
                        </td>
                        <td data-label="Duration">
                          {row.duration_days != null
                            ? <span className="num">{row.duration_days} day{row.duration_days === 1 ? '' : 's'}</span>
                            : <span className="text-faint">—</span>}
                        </td>
                        <td data-label="Tier">
                          {row.tier_id == null
                            ? <span className="text-faint">Default tier</span>
                            : <Badge tone="accent">{tierName(row.tier_id)}</Badge>}
                        </td>
                        <td data-label="Actions">
                          <div className="row gap-1 row-end">
                            <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                    aria-label={`QR code for coupon ${row.code}`} title="QR code"
                                    aria-haspopup="dialog" onClick={() => setQrFor(row)}>
                              <Icon name="qr" />
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                    aria-label={`Edit coupon ${row.code}`} title="Edit coupon"
                                    onClick={() => setModal({ mode: 'edit', initial: row })}>
                              <Icon name="edit" />
                            </button>
                            <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                    aria-label={`Delete coupon ${row.code}`} title="Delete coupon"
                                    onClick={() => remove(row)}>
                              <Icon name="trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Only renders when the filtered set spans more than one page. */}
            <Pagination page={safePage} pageSize={PAGE_SIZE} total={filteredRows.length}
                        onPage={setPage} noun="coupons" />
          </>
        )}
      </div>

      {modal && (
        <CouponModal
          mode={modal.mode}
          initial={modal.initial}
          tiers={tiers}
          onCancel={() => setModal(null)}
          onSubmit={(body) => modal.mode === 'edit' ? handleUpdate(modal.initial.id, body) : handleCreate(body)}
        />
      )}

      {qrFor && <CouponQrModal coupon={qrFor} onClose={() => setQrFor(null)} />}

      {confirmDialog}
    </div>
  );
}

export default Coupons;
